"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { ArrowLeft, ArrowRight, Bot, Calendar, CircleCheck as CheckCircle2, ChevronRight, Compass, Cpu, Flag, Globe as Globe2, GraduationCap, Info, Laptop, Layers, MapPin, Presentation, Quote, Sparkles, Target, Trophy, Users, Workflow, Wrench } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
interface TranslationContent {
  breadcrumb: {
    home: string;
    pathway: string;
    track: string;
    current: string;
  };
  courseLabel: string;
  courseTitle: string;
  conceptQuote: string;
  heroCtaPrimary: string;
  heroCtaSecondary: string;
  audienceHeading: string;
  audienceSubtitle: string;
  audienceDesc: string;
  audiencePoints: string[];
  specsHeading: string;
  specAge: string;
  specPlatform: string;
  specDuration: string;
  specGroup: string;
  specPrereq: string;
  timelineHeading: string;
  timelineSubtitle: string;
  timelineStages: {
    stage: string;
    sessions: string;
    title: string;
    desc: string;
  }[];
  methodologyHeading: string;
  methodologySubtitle: string;
  methodologySteps: {
    step: string;
    title: string;
    desc: string;
  }[];
  hardwareHeading: string;
  hardwareSubtitle: string;
  hardwarePlatformName: string;
  hardwareCostLabel: string;
  hardwareCostValue: string;
  hardwareCostNote: string;
  hardwareRequirementLabel: string;
  hardwareRequirementValue: string;
  hardwareFeatures: string[];
  groupModelHeading: string;
  groupModelSubtitle: string;
  groupModels: {
    title: string;
    tag: string;
    ratio: string;
    desc: string;
  }[];
  focusHeading: string;
  focusSubtitle: string;
  skills: {
    title: string;
    desc: string;
  }[];
  capstoneHeading: string;
  capstoneSubtitle: string;
  capstoneBadge: string;
  capstoneDesc: string;
  capstonePoints: string[];
  roadmapHeading: string;
  roadmapSubtitle: string;
  nextStepHeading: string;
  nextStepTitle: string;
  nextStepDesc: string;
  nextStepCta: string;
  previousCourse: string;
  nextCourse: string;
}
const CONTENT: Record<"vi" | "en", TranslationContent> = {
  vi: {
    breadcrumb: {
      home: "Trang chủ",
      pathway: "Lộ trình Robotics",
      track: "Robotics 7+ tuổi",
      current: "HP3 – Xe robot đầu tiên"
    },
    courseLabel: "HỌC PHẦN HP3",
    courseTitle: "Xe robot đầu tiên",
    conceptQuote: "Làm chủ chuyển động của robot di động.",
    heroCtaPrimary: "ĐĂNG KÝ TƯ VẤN",
    heroCtaSecondary: "XEM TOÀN BỘ LỘ TRÌNH",
    audienceHeading: "Khóa học này dành cho ai?",
    audienceSubtitle: "Chuyển tiếp nền tảng sang robot tự hành di động",
    audienceDesc: "Dành cho học viên đã hoàn thành HP2. HP3 giới thiệu khung xe robot chuẩn và chuyển động chính xác.",
    audiencePoints: ["Học viên đã hoàn thành trọn vẹn học phần HP2.", "Làm quen với hệ khung gầm xe robot di động chuẩn hóa.", "Kiểm soát chính xác các chuyển động cơ bản: tiến, lùi, quay góc và bù sai số thực tế.", "Xây dựng tư duy thuật toán định tuyến làm tiền đề cho cảm biến thông minh ở HP4."],
    specsHeading: "Thông số khoá học",
    specAge: "Từ 7 tuổi",
    specPlatform: "WhalesBot AI Module 1S",
    specDuration: "12 buổi × 90 phút",
    specGroup: "2–3 học sinh/bộ kit",
    specPrereq: "Hoàn thành HP2",
    timelineHeading: "Cấu trúc 12 buổi học",
    timelineSubtitle: "Lộ trình 6 giai đoạn phát triển toàn diện kỹ năng xe robot",
    timelineStages: [{
      stage: "Giai đoạn 1",
      sessions: "Buổi 1 – 2",
      title: "Khung xe robot chuẩn (Standard chassis)",
      desc: "Lắp ráp hệ khung xe chuẩn hóa dùng chung, căn chỉnh cân bằng trọng tâm và trục bánh vi sai."
    }, {
      stage: "Giai đoạn 2",
      sessions: "Buổi 3 – 4",
      title: "Chuyển động chính xác tiến & lùi (Precise movement)",
      desc: "Lập trình di chuyển tiến/lùi chính xác, đồng bộ hóa tốc độ động cơ và hiệu chuẩn độ bám sàn."
    }, {
      stage: "Giai đoạn 3",
      sessions: "Buổi 5 – 6",
      title: "Góc quay 90° / 180° / 360° (Turning)",
      desc: "Lập trình các kỹ thuật quay tại chỗ và quay bánh trục với góc 90°, 180° và 360° chuẩn xác."
    }, {
      stage: "Giai đoạn 4",
      sessions: "Buổi 7 – 8",
      title: "Chu vi bánh xe & Quãng đường (Wheel circumference & Distance)",
      desc: "Ứng dụng công thức chu vi bánh xe để tính toán quãng đường dịch chuyển chính xác theo từng centimet."
    }, {
      stage: "Giai đoạn 5",
      sessions: "Buổi 9 – 10",
      title: "Hiệu chỉnh sai số & Vòng lặp (Error correction & Loops)",
      desc: "Đo đạc độ lệch quán tính, lập trình bù sai số thực tế và tối ưu hóa cấu trúc lệnh lặp lại."
    }, {
      stage: "Giai đoạn 6",
      sessions: "Buổi 11 – 12",
      title: "Lập kế hoạch lộ trình & Khối lệnh My Blocks (Route planning & My Blocks)",
      desc: "Phân rã lộ trình phức tạp và đóng gói các hàm di chuyển chuẩn thành khối lệnh My Blocks tái sử dụng."
    }],
    methodologyHeading: "Phương pháp học 4 bước",
    methodologySubtitle: "Quy trình tư duy kỹ sư chuẩn quốc tế xuyên suốt mọi buổi học",
    methodologySteps: [{
      step: "01",
      title: "Khởi động & Phân tích",
      desc: "Tiếp nhận bài toán chuyển động thực tế, phân tích mục tiêu tọa độ và vật lý di chuyển."
    }, {
      step: "02",
      title: "Lắp ráp khung gầm",
      desc: "Thực hành lắp ghép khung xe chuẩn theo sơ đồ kỹ thuật, bảo đảm cân bằng cơ khí."
    }, {
      step: "03",
      title: "Lập trình & Bù sai số",
      desc: "Viết mã lệnh di chuyển, tính toán chu vi bánh xe, thử nghiệm và bù góc lệch trên sa bàn."
    }, {
      step: "04",
      title: "Vận hành & Thuyết trình",
      desc: "Vận hành robot độc lập trên sa bàn kiểm định, chia sẻ giải pháp thuật toán trước lớp."
    }],
    hardwareHeading: "Thiết bị & Nền tảng học tập",
    hardwareSubtitle: "Bộ công cụ công nghệ chuẩn mực cùng yêu cầu thiết bị đồng bộ",
    hardwarePlatformName: "WhalesBot AI Module 1S",
    hardwareCostLabel: "Chi phí trang thiết bị tham khảo",
    hardwareCostValue: "~6.500.000 VNĐ / bộ kit",
    hardwareCostNote: "Lưu ý: Đây là chi phí trang thiết bị tham khảo, không phải học phí.",
    hardwareRequirementLabel: "Yêu cầu máy tính",
    hardwareRequirementValue: "Yêu cầu máy tính xách tay (Laptop/Computer required)",
    hardwareFeatures: ["Bộ vi điều khiển trung tâm hỗ trợ giao tiếp tốc độ cao", "Động cơ servo kỹ thuật số phản hồi góc quay chuẩn xác", "Bánh xe vi sai bám sàn cao kèm bánh dẫn hướng linh hoạt", "Môi trường phần mềm lập trình khối mô-đun hóa trực quan"],
    groupModelHeading: "Mô hình học nhóm nhỏ",
    groupModelSubtitle: "Tối ưu hóa thời lượng chạm tay vào thiết bị và tương tác sư phạm",
    groupModels: [{
      title: "Mô hình Lý tưởng",
      tag: "Khuyến nghị",
      ratio: "2 học sinh / bộ kit",
      desc: "Tối đa hóa thời lượng tương tác thực hành trực tiếp, một bạn tập trung cơ cấu cơ khí và một bạn tinh chỉnh thuật toán."
    }, {
      title: "Mô hình Tối ưu chi phí",
      tag: "Linh hoạt",
      ratio: "3 học sinh / bộ kit",
      desc: "Tối ưu chi phí trang bị, phân vai rõ ràng giữa cơ khí - lập trình viên - kiểm thử viên sa bàn theo chu trình luân phiên."
    }],
    focusHeading: "Trọng tâm kiến thức & kỹ năng",
    focusSubtitle: "8 năng lực cơ bản định hình nền tảng kỹ sư xe robot tự hành",
    skills: [{
      title: "Khung gầm chuẩn (Standard chassis)",
      desc: "Lắp ráp hệ truyền động 2 bánh vi sai vững chắc, cân bằng trọng tâm xe."
    }, {
      title: "Chuyển động chính xác (Precise movement)",
      desc: "Hiệu chuẩn sai số tốc độ và độ bám bánh xe khi tiến/lùi theo quỹ đạo thẳng."
    }, {
      title: "Góc quay 90° / 180° / 360° (Turning)",
      desc: "Lập trình quay tại chỗ (spin turn) và quay vòng cung (pivot turn) tuyệt đối."
    }, {
      title: "Quãng đường & Chu vi bánh (Wheel circumference / distance)",
      desc: "Ứng dụng chu vi bánh xe vào tính toán quãng đường dịch chuyển chính xác."
    }, {
      title: "Hiệu chỉnh sai số (Error correction)",
      desc: "Phát hiện độ lệch vật lý và lập trình bù góc xoay khi robot di chuyển."
    }, {
      title: "Lập kế hoạch lộ trình (Route planning)",
      desc: "Phân rã hành trình phức tạp thành các phân đoạn chuyển động tối ưu."
    }, {
      title: "Vòng lặp (Loops)",
      desc: "Tối ưu hoá chuỗi hành động lặp đi lặp lại trên sa bàn nhiệm vụ."
    }, {
      title: "Khối lệnh tuỳ biến (My Blocks)",
      desc: "Mô-đun hoá các hàm chuyển động chuẩn để tái sử dụng linh hoạt."
    }],
    capstoneHeading: "Hoạt động / Thử thách cuối khóa",
    capstoneSubtitle: "Hoạt động tổng kết đánh giá năng lực tích hợp cơ học và thuật toán",
    capstoneBadge: "THỬ THÁCH TỔNG KẾT",
    capstoneDesc: "Học viên tham gia thử thách tổng kết di chuyển điều hướng sa bàn lưới (city-grid delivery challenge): lập trình xe robot tự hành xuất phát từ trạm trung tâm, tính toán quãng đường và góc quay để lần lượt hoàn thành các chặng giao vận qua mạng lưới nút giao vuông góc mà không cần can thiệp thủ công.",
    capstonePoints: ["Vận hành xe robot tự hành bám sát bản đồ sa bàn lưới", "Tính toán chính xác quãng đường dựa trên số vòng quay bánh xe", "Thực hiện chuỗi góc quay 90° và 180° chính xác tại từng nút giao", "Đóng gói quy trình di chuyển vào các khối lệnh My Blocks tái sử dụng"],
    roadmapHeading: "Vị trí trong lộ trình đào tạo",
    roadmapSubtitle: "Chuỗi 10 học phần tuần tự từ nền tảng mầm non đến AI chuyên sâu",
    nextStepHeading: "Bước tiếp theo trong lộ trình",
    nextStepTitle: "HP4 – Xe robot thông minh",
    nextStepDesc: "Sau khi làm chủ động học khung gầm ở HP3, học viên sẽ tích hợp các cảm biến môi trường (siêu âm, dò vạch quang học) để xe robot tự thích ứng và tránh vật cản linh hoạt.",
    nextStepCta: "XEM HP4 →",
    previousCourse: "← HP2 — Robot biết cảm nhận",
    nextCourse: "HP4 — Xe robot thông minh →"
  },
  en: {
    breadcrumb: {
      home: "Home",
      pathway: "Robotics Pathway",
      track: "Robotics Ages 7+",
      current: "HP3 – My First Wheeled Robot"
    },
    courseLabel: "MODULE HP3",
    courseTitle: "My First Wheeled Robot",
    conceptQuote: "Mastering mobile robot movement.",
    heroCtaPrimary: "BOOK CONSULTATION",
    heroCtaSecondary: "VIEW FULL PATHWAY",
    audienceHeading: "Who is this course for?",
    audienceSubtitle: "Foundational transition to autonomous wheeled robotics",
    audienceDesc: "For learners who completed HP2. HP3 introduces the standard robot chassis and precise movement control.",
    audiencePoints: ["Learners who have successfully completed Module HP2.", "Ready to adopt a standardized mobile robot drivetrain chassis.", "Mastering core navigation: linear travel, rotational angles, and real-world drift correction.", "Forming structured algorithmic planning ahead of multi-sensor integration in HP4."],
    specsHeading: "Course Specifications",
    specAge: "Ages 7+",
    specPlatform: "WhalesBot AI Module 1S",
    specDuration: "12 sessions × 90 mins",
    specGroup: "2–3 students/kit",
    specPrereq: "Completed HP2",
    timelineHeading: "12-Session Structure Timeline",
    timelineSubtitle: "6 progressive stages covering complete wheeled robot kinematics",
    timelineStages: [{
      stage: "Stage 1",
      sessions: "Sessions 1 – 2",
      title: "Shared standard chassis",
      desc: "Assemble the shared standardized chassis, calibrate center of mass, and align differential drive axles."
    }, {
      stage: "Stage 2",
      sessions: "Sessions 3 – 4",
      title: "Precise movement forward/backward",
      desc: "Program exact linear travel forward and backward, synchronize motors, and evaluate wheel floor friction."
    }, {
      stage: "Stage 3",
      sessions: "Sessions 5 – 6",
      title: "Turning 90° / 180° / 360°",
      desc: "Program zero-radius spin turns and wheel-pivot turns calibrated precisely at 90°, 180°, and 360°."
    }, {
      stage: "Stage 4",
      sessions: "Sessions 7 – 8",
      title: "Wheel circumference & Distance",
      desc: "Apply wheel circumference geometry to accurately compute point-to-point travel distance in centimeters."
    }, {
      stage: "Stage 5",
      sessions: "Sessions 9 – 10",
      title: "Error correction & Loops",
      desc: "Measure real-world inertia drift, apply offset compensation routines, and condense repeated sequences via loops."
    }, {
      stage: "Stage 6",
      sessions: "Sessions 11 – 12",
      title: "Route planning & My Blocks",
      desc: "Deconstruct multi-waypoint navigation challenges and encapsulate core routines into reusable My Blocks."
    }],
    methodologyHeading: "4-Step Learning Method",
    methodologySubtitle: "Structured engineering inquiry cycle applied in every single session",
    methodologySteps: [{
      step: "01",
      title: "Explore & Analyze",
      desc: "Deconstruct real-world kinematic challenges, clarify movement goals, and calculate target coordinates."
    }, {
      step: "02",
      title: "Build Chassis",
      desc: "Assemble the standardized chassis following technical blueprints to guarantee mechanical rigidity."
    }, {
      step: "03",
      title: "Program & Calibrate",
      desc: "Formulate movement blocks, calculate circumference formulas, test routines, and calibrate drift offsets."
    }, {
      step: "04",
      title: "Deploy & Present",
      desc: "Run autonomous missions across test arenas and present engineering decisions in front of the cohort."
    }],
    hardwareHeading: "Platform & Equipment",
    hardwareSubtitle: "Standardized robotics kit and synchronous computing requirements",
    hardwarePlatformName: "WhalesBot AI Module 1S",
    hardwareCostLabel: "Reference equipment cost",
    hardwareCostValue: "~6,500,000 VNĐ / kit",
    hardwareCostNote: "Note: This is reference equipment cost, not tuition.",
    hardwareRequirementLabel: "Computer Requirement",
    hardwareRequirementValue: "Computer required (Laptop/PC required for programming)",
    hardwareFeatures: ["High-speed microprocessor hub with multi-port motor control", "Precision digital servo motors with real-time rotational feedback", "Differential high-grip rubber drive wheels plus omni-caster support", "WhalesBot graphical modular block programming workspace"],
    groupModelHeading: "Small-Group Learning Model",
    groupModelSubtitle: "Optimizing hands-on tool engagement and cooperative peer learning",
    groupModels: [{
      title: "Ideal Setup",
      tag: "Recommended",
      ratio: "2 students / kit",
      desc: "Maximizes hands-on tactile construction and software interaction, balancing mechanical build and algorithmic testing."
    }, {
      title: "Cost-Optimized Setup",
      tag: "Flexible",
      ratio: "3 students / kit",
      desc: "Cost-effective collaborative model with rotating roles: mechanical engineer, software developer, and arena validator."
    }],
    focusHeading: "Core Competencies & Focus",
    focusSubtitle: "8 essential skills establishing autonomous mobile robotics mastery",
    skills: [{
      title: "Standard chassis",
      desc: "Build rigid dual-wheel differential drivetrains with optimized center of gravity."
    }, {
      title: "Precise movement (Forward/backward)",
      desc: "Calibrate traction and speed variations along linear trajectories."
    }, {
      title: "Turning 90° / 180° / 360°",
      desc: "Program repeatable pivot turns and zero-radius point turns."
    }, {
      title: "Wheel circumference & Distance",
      desc: "Apply wheel geometry formulas to calculate accurate travel distances."
    }, {
      title: "Error correction",
      desc: "Diagnose mechanical drift and implement software offset compensation."
    }, {
      title: "Route planning",
      desc: "Deconstruct multi-waypoint missions into sequenced autonomous routes."
    }, {
      title: "Loops",
      desc: "Streamline code structure for recurring navigational patterns."
    }, {
      title: "My Blocks (Custom blocks)",
      desc: "Encapsulate navigational routines into reusable modular subroutines."
    }],
    capstoneHeading: "Capstone Activity",
    capstoneSubtitle: "Integrated final challenge assessing mechanical and algorithmic competence",
    capstoneBadge: "CAPSTONE ACTIVITY",
    capstoneDesc: "Students tackle a city-grid delivery challenge: programming an autonomous wheeled robot from a central dispatch depot to sequentially visit destination coordinates across perpendicular grid intersections without manual intervention.",
    capstonePoints: ["Autonomously navigate designated coordinate waypoints across a grid arena", "Leverage wheel circumference formulas to stop precisely at target delivery spots", "Execute exact 90° and 180° turns within constricted intersection lanes", "Package navigational commands into reusable My Blocks subroutines"],
    roadmapHeading: "Position in Pathway",
    roadmapSubtitle: "Sequential 10-module progression from foundational STEM to advanced AI robotics",
    nextStepHeading: "What's Next In The Pathway?",
    nextStepTitle: "HP4 – Smart Robot Car",
    nextStepDesc: "After mastering chassis kinematics in HP3, students progress to integrating ultrasonic, optical, and color sensors, transforming their robot into an autonomous environmental explorer.",
    nextStepCta: "XEM HP4 →",
    previousCourse: "← HP2 — Sensing Robots",
    nextCourse: "HP4 — Smart Wheeled Robot →"
  }
};
const PATHWAY_NODES = [{
  id: "D1",
  label: "D1",
  nameVi: "Cơ cấu cơ khí",
  nameEn: "Mechanical Basics",
  to: "/RoboticsCourseD1Page",
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d1.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d1.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d1.png-wvc-sizes"
}, {
  id: "D2",
  label: "D2",
  nameVi: "Truyền động bánh răng",
  nameEn: "Gears & Transmission",
  to: "/RoboticsCourseD2Page",
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d2.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d2.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d2.png-wvc-sizes"
}, {
  id: "D3",
  label: "D3",
  nameVi: "Cơ cấu đòn bẩy & ròng rọc",
  nameEn: "Levers & Pulleys",
  to: "/RoboticsCourseD3Page",
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d3.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d3.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d3.png-wvc-sizes"
}, {
  id: "D4",
  label: "D4",
  nameVi: "Mô hình chuyển động phức hợp",
  nameEn: "Complex Mechanisms",
  to: "/RoboticsCourseD4Page",
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d4.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d4.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d4.png-wvc-sizes"
}, {
  id: "HP1",
  label: "HP1",
  nameVi: "Robot đầu tiên của bé",
  nameEn: "My First Robot",
  to: "/RoboticsCourseHP1Page",
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp1.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp1.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp1.webp-wvc-sizes"
}, {
  id: "HP2",
  label: "HP2",
  nameVi: "Robot biết cảm nhận",
  nameEn: "Sensing Robots",
  to: "/RoboticsCourseHP2Page",
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp2.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp2.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp2.png-wvc-sizes"
}, {
  id: "HP3",
  label: "HP3",
  nameVi: "Xe robot đầu tiên",
  nameEn: "First Wheeled Robot",
  to: "/RoboticsCourseHP3Page",
  current: true,
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp3.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp3.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp3.png-wvc-sizes"
}, {
  id: "HP4",
  label: "HP4",
  nameVi: "Xe robot thông minh",
  nameEn: "Smart Wheeled Robot",
  to: "/RoboticsCourseHP4Page",
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp4.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp4.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp4.png-wvc-sizes"
}, {
  id: "HP5",
  label: "HP5",
  nameVi: "Cánh tay robot",
  nameEn: "Robotic Arms",
  to: "/RoboticsCourseHp5Page",
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp5.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp5.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp5.png-wvc-sizes"
}, {
  id: "HP6",
  label: "HP6",
  nameVi: "Robot AI nâng cao",
  nameEn: "Advanced AI Robotics",
  to: "/RoboticsCourseHP6Page",
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp6.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp6.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp6.png-wvc-sizes"
}];
export default function CourseOverview() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  const t = CONTENT[lang];
  return <section id="roboticscoursehp3overview" data-nav="light" className="bg-background text-foreground py-12 md:py-20">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* (1) Top Bar: Full 4-level Breadcrumb + Language Toggle */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 text-xs sm:text-sm text-muted-foreground">
            <Link to="/" className="hover:text-primary transition-colors font-medium hover:underline underline-offset-4">
              {t.breadcrumb.home}
            </Link>
            <ChevronRight className="size-3.5 text-muted-foreground/60 shrink-0" />
            <Link to="/RoboticsLearningPathwayPage" className="hover:text-primary transition-colors font-medium hover:underline underline-offset-4">
              {t.breadcrumb.pathway}
            </Link>
            <ChevronRight className="size-3.5 text-muted-foreground/60 shrink-0" />
            <Link to="/RoboticsLearningPathwayPage" className="hover:text-primary transition-colors font-medium hover:underline underline-offset-4">
              {t.breadcrumb.track}
            </Link>
            <ChevronRight className="size-3.5 text-muted-foreground/60 shrink-0" />
            <span className="font-semibold text-foreground">
              {t.breadcrumb.current}
            </span>
          </nav>

          <div className="flex items-center gap-2 self-start md:self-auto border border-border rounded-lg p-1 bg-card shadow-2xs">
            <Globe2 className="size-4 ml-2 text-muted-foreground shrink-0" />
            <Button variant={lang === "vi" ? "default" : "ghost"} size="sm" className="h-7 px-3 text-xs rounded-md font-medium" onClick={() => setLang("vi")}>
              Tiếng Việt
            </Button>
            <Button variant={lang === "en" ? "default" : "ghost"} size="sm" className="h-7 px-3 text-xs rounded-md font-medium" onClick={() => setLang("en")}>
              English
            </Button>
          </div>
        </div>

        {/* (2) Hero Banner with WhalesBot Mobile Robot Drivetrain Visual */}
        <div className="relative overflow-hidden rounded-xl bg-card border border-border p-6 sm:p-8 md:p-10 mb-12 shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7 flex flex-col justify-between">
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <Badge variant="default" className="text-xs font-bold px-3 py-1 bg-primary text-primary-foreground rounded-md">
                  HP3
                </Badge>
                <span className="text-xs uppercase tracking-widest font-bold text-secondary">
                  {t.courseLabel}
                </span>
              </div>

              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground font-serif mb-4">
                {t.courseTitle}
              </h1>

              <div className="mt-1 flex items-start gap-3 rounded-lg bg-muted/60 border border-border p-4 sm:p-5">
                <Quote className="size-5 md:size-6 text-primary shrink-0 mt-0.5" />
                <blockquote className="text-base md:text-lg font-medium italic text-foreground/90">
                  “{t.conceptQuote}”
                </blockquote>
              </div>

              {/* Hero CTAs */}
              <div className="mt-8 flex flex-wrap items-center gap-3 sm:gap-4">
                <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold shadow-xs rounded-lg px-6">
                  <Link to="/FreeTrialContactPage">
                    <span>{t.heroCtaPrimary}</span>
                    <ArrowRight className="size-4 shrink-0 ml-1" />
                  </Link>
                </Button>

                <Button asChild variant="outline" size="lg" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground font-semibold rounded-lg px-6">
                  <Link to="/RoboticsLearningPathwayPage">
                    <span>{t.heroCtaSecondary}</span>
                  </Link>
                </Button>
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <div className="relative overflow-hidden rounded-xl border border-border bg-muted/30 p-2 shadow-xs group">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/whalesbot_wheeled_chassis_hero.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/whalesbot_wheeled_chassis_hero.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/whalesbot_wheeled_chassis_hero.png-wvc-sizes" alt="WhalesBot mobile robot chassis with differential wheels and high-torque motors" loading="lazy" className="w-full h-auto object-cover rounded-lg aspect-4/3 transition-transform duration-300 group-hover:scale-[1.02]" />
              </div>
            </div>
          </div>
        </div>

        {/* (3) 'Who is this course for?' section */}
        <div className="mb-12">
          <Card className="border-border bg-card shadow-xs rounded-xl overflow-hidden">
            <CardHeader className="pb-3 border-b border-border/60 bg-muted/30">
              <div className="flex items-center gap-2">
                <Target className="size-5 text-primary shrink-0" />
                <CardTitle className="text-xl md:text-2xl font-serif font-bold text-foreground">
                  {t.audienceHeading}
                </CardTitle>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {t.audienceSubtitle}
              </p>
            </CardHeader>
            <CardContent className="pt-6">
              <p className="text-base text-foreground/90 mb-6 leading-relaxed font-medium">
                {t.audienceDesc}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                {t.audiencePoints.map((point, index) => <div key={index} data-index={index} className="flex items-start gap-3 p-3.5 rounded-lg border border-border/70 bg-muted/20">
                    <CheckCircle2 className="size-5 text-secondary shrink-0 mt-0.5" />
                    <span className="text-sm text-foreground/90 leading-snug">
                      {point}
                    </span>
                  </div>)}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Course Specifications Quick Matrix */}
        <div className="mb-12">
          <h2 className="text-xl md:text-2xl font-bold text-foreground mb-6 font-serif flex items-center gap-2">
            <Cpu className="size-5 text-primary" />
            {t.specsHeading}
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <Card className="border-border bg-card shadow-xs rounded-lg">
              <CardContent className="pt-6 flex flex-col gap-2">
                <div className="p-2.5 rounded-lg bg-primary/10 text-primary w-fit">
                  <GraduationCap className="size-5" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                    {lang === "vi" ? "Độ tuổi" : "Age"}
                  </p>
                  <p className="text-base font-semibold text-foreground mt-0.5">
                    {t.specAge}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border bg-card shadow-xs rounded-lg">
              <CardContent className="pt-6 flex flex-col gap-2">
                <div className="p-2.5 rounded-lg bg-secondary/20 text-secondary-foreground w-fit">
                  <Bot className="size-5" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                    {lang === "vi" ? "Nền tảng" : "Platform"}
                  </p>
                  <p className="text-base font-semibold text-foreground mt-0.5">
                    {t.specPlatform}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border bg-card shadow-xs rounded-lg">
              <CardContent className="pt-6 flex flex-col gap-2">
                <div className="p-2.5 rounded-lg bg-accent/20 text-foreground w-fit">
                  <SafeHydrate><Calendar className="size-5" /></SafeHydrate>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                    {lang === "vi" ? "Thời lượng" : "Duration"}
                  </p>
                  <p className="text-base font-semibold text-foreground mt-0.5">
                    {t.specDuration}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border bg-card shadow-xs rounded-lg">
              <CardContent className="pt-6 flex flex-col gap-2">
                <div className="p-2.5 rounded-lg bg-primary/10 text-primary w-fit">
                  <Users className="size-5" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                    {lang === "vi" ? "Quy mô nhóm" : "Group Size"}
                  </p>
                  <p className="text-base font-semibold text-foreground mt-0.5">
                    {t.specGroup}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border bg-card shadow-xs rounded-lg">
              <CardContent className="pt-6 flex flex-col gap-2">
                <div className="p-2.5 rounded-lg bg-secondary/20 text-secondary-foreground w-fit">
                  <CheckCircle2 className="size-5" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                    {lang === "vi" ? "Điều kiện tiên quyết" : "Prerequisite"}
                  </p>
                  <p className="text-base font-semibold text-foreground mt-0.5">
                    {t.specPrereq}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* (4) 12-session timeline grouped into 6 stages */}
        <div className="mb-12">
          <div className="mb-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground font-serif flex items-center gap-2">
              <SafeHydrate><Calendar className="size-5 text-primary" /></SafeHydrate>
              {t.timelineHeading}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {t.timelineSubtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {t.timelineStages.map((stage, index) => <Card key={index} data-index={index} className="border-border bg-card shadow-2xs rounded-lg flex flex-col justify-between">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className="text-xs font-bold px-2 py-0.5 rounded bg-primary/10 text-primary">
                      {stage.stage}
                    </span>
                    <span className="text-xs font-semibold text-muted-foreground">
                      {stage.sessions}
                    </span>
                  </div>
                  <CardTitle className="text-base font-semibold font-serif text-foreground">
                    {stage.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {stage.desc}
                  </p>
                </CardContent>
              </Card>)}
          </div>
        </div>

        {/* (5) 4-step learning method section matching D1-D4 pattern */}
        <div className="mb-12">
          <div className="mb-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground font-serif flex items-center gap-2">
              <Workflow className="size-5 text-primary" />
              {t.methodologyHeading}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {t.methodologySubtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {t.methodologySteps.map((step, index) => {
            const icons = [Compass, Wrench, Cpu, Presentation];
            const StepIcon = icons[index % icons.length];
            return <Card key={index} data-index={index} className="border-border bg-card shadow-2xs rounded-lg relative overflow-hidden">
                  <CardContent className="pt-6 flex flex-col gap-3">
                    <div className="flex items-center justify-between">
                      <div className="p-2.5 rounded-lg bg-secondary/15 text-secondary">
                        <StepIcon className="size-5" />
                      </div>
                      <span className="text-2xl font-bold font-serif text-muted-foreground/40">
                        {step.step}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-base font-bold font-serif text-foreground">
                        {step.title}
                      </h3>
                      <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                        {step.desc}
                      </p>
                    </div>
                  </CardContent>
                </Card>;
          })}
          </div>

          {/* Technical Illustration: Turning angle technique (90°/180°/360° spin & pivot turns) */}
          <div className="mt-8 relative overflow-hidden rounded-xl border border-border bg-card p-4 sm:p-6 shadow-xs">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
              <div className="lg:col-span-7 flex flex-col justify-center">
                <Badge variant="outline" className="w-fit mb-2 border-primary text-primary font-bold text-xs">
                  {lang === "vi" ? "KỸ THUẬT QUAY ĐIỀU HƯỚNG" : "TURNING TECHNIQUE"}
                </Badge>
                <h3 className="text-lg md:text-xl font-bold font-serif text-foreground mb-2">
                  {lang === "vi" ? "Kỹ thuật góc quay 90° / 180° / 360°" : "90° / 180° / 360° Turning Angle Technique"}
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  {lang === "vi" ? "Học viên HP3 thực hành lập trình kiểm soát góc quay chính xác: quay tại chỗ (spin turn) và quay vòng cung (pivot turn) thông qua điều khiển tỷ lệ tốc độ động cơ vi sai hai bánh." : "HP3 students master precise turning kinematics: executing spin turns and pivot turns through differential speed calibration across drive wheels."}
                </p>
              </div>
              <div className="lg:col-span-5">
                <div className="relative overflow-hidden rounded-lg border border-border bg-muted/30 p-2 group">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/whalesbot_hp3_turning_angle_technique.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/whalesbot_hp3_turning_angle_technique.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/whalesbot_hp3_turning_angle_technique.png-wvc-sizes" alt="Illustration of 90-degree, 180-degree and 360-degree turning techniques for a two-wheel differential-drive robot" loading="lazy" className="w-full h-auto object-cover rounded-md aspect-16/9 transition-transform duration-300 group-hover:scale-[1.02]" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* (6) Equipment card: WhalesBot AI Module 1S, reference equipment cost ~6,500,000 VNĐ/kit, computer required */}
        <div className="mb-12">
          <Card className="border-border bg-card shadow-xs rounded-xl overflow-hidden">
            <CardHeader className="pb-3 border-b border-border/60 bg-muted/20">
              <div className="flex items-center gap-2">
                <Bot className="size-5 text-secondary shrink-0" />
                <CardTitle className="text-xl md:text-2xl font-serif font-bold text-foreground">
                  {t.hardwareHeading}
                </CardTitle>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {t.hardwareSubtitle}
              </p>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                <div className="lg:col-span-5 p-5 rounded-lg bg-muted/30 border border-border flex flex-col gap-4">
                  <div>
                    <span className="text-xs uppercase font-bold tracking-wider text-primary">
                      Robotics Kit
                    </span>
                    <h3 className="text-2xl font-bold font-serif text-foreground mt-1">
                      {t.hardwarePlatformName}
                    </h3>
                  </div>

                  <div className="p-3.5 rounded-lg bg-card border border-border/80">
                    <span className="text-xs text-muted-foreground font-medium block">
                      {t.hardwareCostLabel}
                    </span>
                    <p className="text-lg font-bold text-foreground mt-0.5 text-primary">
                      {t.hardwareCostValue}
                    </p>
                    <div className="flex items-start gap-1.5 mt-2 text-xs text-muted-foreground italic">
                      <Info className="size-3.5 text-primary shrink-0 mt-0.5" />
                      <span>{t.hardwareCostNote}</span>
                    </div>
                  </div>

                  <div className="p-3.5 rounded-lg bg-card border border-border/80 flex items-start gap-2.5">
                    <Laptop className="size-4 text-secondary shrink-0 mt-0.5" />
                    <div>
                      <span className="text-xs text-muted-foreground font-medium block">
                        {t.hardwareRequirementLabel}
                      </span>
                      <p className="text-xs font-semibold text-foreground mt-0.5">
                        {t.hardwareRequirementValue}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-7 flex flex-col gap-4 w-full">
                  <div className="relative overflow-hidden rounded-lg border border-border bg-muted/20 p-2 shadow-2xs group">
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/whalesbot_robotics_kit.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/whalesbot_robotics_kit.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/whalesbot_robotics_kit.png-wvc-sizes" alt="WhalesBot AI Module 1S robotics kit used in ROBOTO's HP1–HP6 engineering track" loading="lazy" className="w-full h-auto object-cover rounded-md aspect-16/9 transition-transform duration-300 group-hover:scale-[1.02]" />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 w-full">
                    {t.hardwareFeatures.map((feature, index) => <div key={index} data-index={index} className="p-4 rounded-lg border border-border/80 bg-card flex items-start gap-3 shadow-2xs">
                        <Sparkles className="size-4 text-accent shrink-0 mt-0.5" />
                        <span className="text-sm font-medium text-foreground leading-snug">
                          {feature}
                        </span>
                      </div>)}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* (7) Small-group learning model section (2 students/kit ideal, 3 students/kit cost-optimized) */}
        <div className="mb-12">
          <div className="mb-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground font-serif flex items-center gap-2">
              <Users className="size-5 text-primary" />
              {t.groupModelHeading}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {t.groupModelSubtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {t.groupModels.map((model, index) => <Card key={index} data-index={index} className="border-border bg-card shadow-2xs rounded-lg p-5 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant="outline" className="text-xs font-semibold border-secondary text-secondary">
                      {model.tag}
                    </Badge>
                    <span className="text-sm font-bold text-primary font-mono">
                      {model.ratio}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold font-serif text-foreground mb-2">
                    {model.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    {model.desc}
                  </p>
                </div>
              </Card>)}
          </div>
        </div>

        {/* Focus & Competencies Grid: Keep the existing 8 skills list */}
        <div className="mb-12">
          <div className="mb-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground font-serif flex items-center gap-2">
              <Compass className="size-5 text-primary" />
              {t.focusHeading}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {t.focusSubtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {t.skills.map((skill, index) => <Card key={index} data-index={index} className="border-border/80 bg-card hover:border-primary/40 transition-colors shadow-2xs rounded-lg">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary text-xs font-bold">
                      {index + 1}
                    </span>
                    <CardTitle className="text-base font-semibold text-foreground">
                      {skill.title}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {skill.desc}
                  </p>
                </CardContent>
              </Card>)}
          </div>
        </div>

        {/* (8) Final project / capstone activity section with City Grid Delivery Map Visual */}
        <div className="mb-12">
          <Card className="border-border bg-card shadow-xs rounded-xl overflow-hidden">
            <CardHeader className="pb-3 border-b border-border/60 bg-muted/20">
              <div className="flex items-center gap-2">
                <Trophy className="size-5 text-accent shrink-0" />
                <CardTitle className="text-xl md:text-2xl font-serif font-bold text-foreground">
                  {t.capstoneHeading}
                </CardTitle>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {t.capstoneSubtitle}
              </p>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
                <div className="lg:col-span-7 flex flex-col gap-4">
                  <div className="rounded-lg bg-muted/30 border border-border/80 p-5">
                    <div className="flex flex-wrap items-center gap-2.5 mb-2">
                      <Badge variant="default" className="bg-primary text-primary-foreground font-bold">
                        {t.capstoneBadge}
                      </Badge>
                    </div>
                    <p className="text-sm text-foreground/90 leading-relaxed">
                      {t.capstoneDesc}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {t.capstonePoints.map((point, index) => <div key={index} data-index={index} className="flex items-start gap-2.5 p-3 rounded-lg border border-border/70 bg-card text-xs text-foreground/90">
                        <Flag className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>{point}</span>
                      </div>)}
                  </div>
                </div>

                <div className="lg:col-span-5">
                  <div className="relative overflow-hidden rounded-xl border border-border bg-muted/30 p-2 shadow-xs group">
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/city_grid_route_capstone_diagram.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/city_grid_route_capstone_diagram.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/city_grid_route_capstone_diagram.png-wvc-sizes" alt="City grid delivery route map diagram with coordinate waypoints and distance calculation vectors" loading="lazy" className="w-full h-auto object-cover rounded-lg aspect-4/3 transition-transform duration-300 group-hover:scale-[1.02]" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* (9) 10-node pathway map with HP3 highlighted */}
        <div className="mb-12">
          <Card className="border-border bg-card shadow-xs rounded-xl overflow-hidden">
            <CardHeader className="pb-3 border-b border-border/60 bg-muted/20">
              <div className="flex items-center gap-2">
                <MapPin className="size-5 text-primary shrink-0" />
                <CardTitle className="text-xl md:text-2xl font-serif font-bold text-foreground">
                  {t.roadmapHeading}
                </CardTitle>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {t.roadmapSubtitle}
              </p>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {PATHWAY_NODES.map((node, index) => {
                const isCurrent = !!node.current;
                return <div key={node.id} data-index={index} className={`relative flex flex-col p-2.5 sm:p-3 rounded-lg border text-left transition-all ${isCurrent ? "border-primary bg-primary/10 shadow-xs ring-2 ring-primary/30" : "border-border bg-card hover:border-primary/50 hover:bg-muted/30"}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded ${isCurrent ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"}`}>
                          {node.label}
                        </span>
                        {isCurrent && <span className="text-[10px] font-bold uppercase tracking-wider text-primary">
                            Current
                          </span>}
                      </div>

                      <div className="relative overflow-hidden rounded-md border border-border/60 bg-muted/30 my-1.5 aspect-4/3 group">
                        <img src={node.img} data-wvc-srcset={node.srcset} data-wvc-sizes={node.sizes} alt={`${node.id} robotics course thumbnail`} loading="lazy" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                      </div>

                      <span className="text-xs font-semibold text-foreground line-clamp-1 mt-0.5">
                        {lang === "vi" ? node.nameVi : node.nameEn}
                      </span>

                      {!isCurrent ? <Link to={node.to} className="mt-2 text-[11px] font-medium text-primary hover:underline inline-flex items-center gap-1">
                          <span>{lang === "vi" ? "Xem khóa" : "View"}</span>
                          <ArrowRight className="size-3" />
                        </Link> : <span className="mt-2 text-[11px] font-medium text-muted-foreground">
                          {lang === "vi" ? "Đang xem" : "Viewing"}
                        </span>}
                    </div>;
              })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* (10) 'What's next' card: next course HP4 – Xe robot thông minh / Smart Robot Car, CTA 'XEM HP4 →' to '/RoboticsCourseHP4Page' */}
        <div className="mb-10">
          <Card className="border-border bg-muted/40 shadow-xs rounded-xl overflow-hidden">
            <CardContent className="p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-secondary">
                  {t.nextStepHeading}
                </span>
                <h3 className="text-2xl font-bold font-serif text-foreground mt-1 mb-2">
                  {t.nextStepTitle}
                </h3>
                <p className="text-sm text-muted-foreground max-w-2xl leading-relaxed">
                  {t.nextStepDesc}
                </p>
              </div>

              <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold rounded-lg shrink-0">
                <Link to="/RoboticsCourseHP4Page">
                  <span>{t.nextStepCta}</span>
                  <ArrowRight className="size-4 shrink-0 ml-1.5" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>

        <Separator className="mb-10" />

        {/* Existing Prev & Next Course Navigation */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
          <Button asChild variant="outline" size="lg" className="border-border hover:bg-muted font-medium justify-between sm:justify-center group rounded-lg">
            <Link to="/RoboticsCourseHP2Page">
              <ArrowLeft className="size-4 shrink-0 group-hover:-translate-x-1 transition-transform" />
              <span>{t.previousCourse}</span>
            </Link>
          </Button>

          <Button asChild variant="default" size="lg" className="font-medium justify-between sm:justify-center group bg-primary text-primary-foreground shadow-xs hover:bg-primary/90 rounded-lg">
            <Link to="/RoboticsCourseHP4Page">
              <span>{t.nextCourse}</span>
              <ArrowRight className="size-4 shrink-0 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}